/* ============================================================
   AURORA 9ABZ TECHNOLOGY PANEL — motion controller (v2)
   Owner brief 2026-07-20: the jar AUTO-plays its sequence when the
   panel enters the viewport — rotation flowing into the lid opening
   and revealing the cream — then holds the open state while the two
   ingredient annotations fade in with their dotted lines.
   No drag, no product name, no CTA.
   Performance: canvas frame scrubber, progressive interleaved frame
   streaming, rAF only during the play-through, IntersectionObserver
   boot. Reduced motion: final open frame + annotations immediately.
   ============================================================ */
(function () {
  'use strict';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initViewer(root) {
    var viewer = root.querySelector('[data-yns-viewer]');
    if (!viewer) return;
    var canvas = viewer.querySelector('.yns-canvas');
    var ctx = canvas.getContext('2d');
    var listEl = viewer.querySelector('[data-yns-frames]');
    var urls;
    try { urls = JSON.parse(listEl.textContent); } catch (e) { return; }
    var total = urls.length;
    if (!total) return;

    var images = new Array(total);
    var loaded = new Array(total);
    var frame = 0;
    var rafId = null;

    function size() {
      var r = viewer.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      draw();
    }

    function draw() {
      var i = Math.max(0, Math.min(total - 1, Math.round(frame)));
      var img = images[i];
      if (!img || !loaded[i]) {
        for (var d = 1; d < total; d++) {
          var b = i - d, a = i + d;
          if (b >= 0 && loaded[b]) { img = images[b]; break; }
          if (a < total && loaded[a]) { img = images[a]; break; }
        }
      }
      if (!img) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }

    function load(i, cb) {
      if (images[i]) { if (cb && loaded[i]) cb(); return; }
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { loaded[i] = true; if (cb) cb(); if (Math.round(frame) === i) draw(); };
      img.src = urls[i];
      images[i] = img;
    }

    function loadAll() {
      var order = [];
      for (var step = Math.floor(total / 8) || 1; step >= 1; step = Math.floor(step / 2)) {
        for (var i = 0; i < total; i += step) if (order.indexOf(i) === -1) order.push(i);
        if (step === 1) break;
      }
      var k = 0;
      (function next() {
        if (k >= order.length) return;
        var idx = order[k++];
        if (images[idx]) { next(); return; }
        load(idx, next);
        setTimeout(next, 40);
      })();
    }

    /* Never-ending cycle (owner 2026-07-20): the jar rotates one slow
       revolution, then the open-jar still cross-fades in over it to show
       the cream inside, holds, fades back out, and the rotation resumes.
       Loops forever. The frame set itself is a closed-jar turntable, so
       the reveal is carried by the still. */
    var openStill = viewer.querySelector('[data-yns-open]');
    function play() {
      if (reducedMotion.matches) {
        frame = 0; draw();
        root.classList.add('is-open');
        viewer.classList.add('is-reveal');
        return;
      }
      var REV_MS = 9000;   /* one slow revolution */
      var FADE_MS = 800;   /* still cross-fade (matches the CSS transition) */
      var HOLD_MS = 2600;  /* cream on show */
      var phase = 'rotate', phaseStart = null, cycles = 0;
      function tick(ts) {
        if (phaseStart === null) phaseStart = ts;
        var el = ts - phaseStart;
        if (phase === 'rotate') {
          frame = (el / REV_MS) * total;
          if (frame >= total) frame = frame % total;
          draw();
          if (el >= REV_MS) {
            cycles++;
            frame = 0; draw();
            if (openStill) { viewer.classList.add('is-reveal'); phase = 'reveal'; }
            phaseStart = ts;
          }
        } else if (phase === 'reveal') {
          if (el >= FADE_MS + HOLD_MS) {
            viewer.classList.remove('is-reveal');
            phase = 'unreveal'; phaseStart = ts;
          }
        } else { /* unreveal: wait for the fade-out, then rotate again */
          if (el >= FADE_MS) { phase = 'rotate'; phaseStart = ts; }
        }
        rafId = requestAnimationFrame(tick);
      }
      rafId = requestAnimationFrame(tick);
    }

    window.addEventListener('resize', size);

    var booted = false;
    function boot() {
      if (booted) return;
      booted = true;
      size();
      load(0, function () {
        draw();
        loadAll();
        var waited = 0;
        (function whenReady() {
          var have = 0;
          for (var i = 0; i < total; i++) if (loaded[i]) have++;
          if (have >= Math.min(total, Math.ceil(total / 3)) || waited > 6000) { play(); }
          else { waited += 120; setTimeout(whenReady, 120); }
        })();
      });
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (en) { if (en.isIntersecting) { boot(); obs.disconnect(); } });
      }, { rootMargin: '300px 0px', threshold: 0.1 }).observe(viewer);
    } else { boot(); }
  }

  /* Owner 2026-07-20: the data points — 106, 39, 67 — and the named
     actives sit in Coral Nine and change colour on mouse-over. Spans are
     injected here so the copy in the theme editor stays plain text. */
  var HL_RE = /(\d+(?:[.,]\d+)?%?|niacinamide|hyaluronic acid|beta-glucan|vitamin [A-Za-z]|collagen amino acids|elasticity|hydration|firmness|barrier & microcirculation|barrier|microcirculation|oxidative vitality)/gi;
  function highlightEl(el) {
    if (!el || el.dataset.ynsHl) return;
    el.dataset.ynsHl = '1';
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var text = node.nodeValue;
      if (!HL_RE.test(text)) { HL_RE.lastIndex = 0; return; }
      HL_RE.lastIndex = 0;
      var frag = document.createDocumentFragment();
      var last = 0, m;
      while ((m = HL_RE.exec(text)) !== null) {
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        var span = document.createElement('span');
        span.className = 'yns-hl';
        span.textContent = m[0];
        frag.appendChild(span);
        last = m.index + m[0].length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-yns]');
    for (var i = 0; i < roots.length; i++) {
      (function (root) {
        /* copy column only — fading .yns-stage would give it a temporary
           stacking context (opacity < 1) that breaks the canvas blend and
           flashes the white square during the entrance */
        var targets = root.querySelectorAll('.yns-copycol');
        for (var t = 0; t < targets.length; t++) {
          targets[t].classList.add('yns-anim-target');
          targets[t].style.transitionDelay = (t * 0.15) + 's';
        }
        if ('IntersectionObserver' in window && !reducedMotion.matches) {
          new IntersectionObserver(function (entries, obs) {
            entries.forEach(function (en) {
              if (en.isIntersecting) {
                root.classList.add('is-in');
                /* NATURE / SCIENCE fade in ~0.5s after the panel scrolls
                   into view (owner 2026-07-20) — not tied to the rotation */
                setTimeout(function () { root.classList.add('is-open'); }, 500);
                obs.disconnect();
              }
            });
          }, { threshold: 0.12 }).observe(root);
        } else { root.classList.add('is-in'); root.classList.add('is-open'); }
        var hlTargets = root.querySelectorAll('.yns-headline, .yns-stattext, .yns-statnum, .yns-ann-text');
        for (var h = 0; h < hlTargets.length; h++) highlightEl(hlTargets[h]);
        initViewer(root);
      })(roots[i]);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
